// ============================================================================
// MeetingDetailPage — fiche rencontre CG (E3b, F-437..F-443) — /rencontres/:id
// ============================================================================
// Edit admin (datetime-local Montreal), cancel via status, soft-delete ConfirmDialog,
// documents = download only (jamais inline) + upload admin.

import { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, ConfirmDialog, QueryError, SkeletonLine, SkeletonCard,
  GS_COMPONENTS,
} from '@gs/ui';
import { ArrowLeft, Download, FileText, Inbox, ShieldAlert, Trash2 } from 'lucide-react';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useMeetingDetail, useUpdateMeeting, useUpdateMeetingStatus, useDeleteMeeting,
  useUploadMeetingDocument, useDeleteMeetingDocument,
  meetingErrorCode, type MeetingDetailResponse, type MeetingStatus,
} from '../../hooks/useMeetings';
import { MeetingStatusBadge, MeetingsForbidden } from './RencontresPage';
import { formatZonedDateTime } from '../../lib/time/zone';
import { downloadBlobAs } from '../../lib/blobs/objectUrl';

const DOC_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

function EditMeetingPanel({
  detail,
  onError,
}: {
  detail: MeetingDetailResponse;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateMeeting(detail.meeting.id);
  const setStatus = useUpdateMeetingStatus(detail.meeting.id);
  const remove = useDeleteMeeting(detail.meeting.id);

  const [title, setTitle] = useState(detail.meeting.title);
  const [scheduledAt, setScheduledAt] = useState(detail.meeting.scheduledAtLocal);
  const [durationMinutes, setDurationMinutes] = useState(detail.meeting.durationMinutes ?? 90);
  const [location, setLocation] = useState(detail.meeting.location ?? '');
  const [status, setStatusLocal] = useState<MeetingStatus>(detail.meeting.status);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  function save() {
    update.mutate(
      {
        title: title.trim(),
        scheduledAt,
        durationMinutes,
        location: location.trim() ? location.trim() : null,
        status,
      },
      { onError: (err) => onError(meetingErrorCode(err)) },
    );
  }

  function doCancel() {
    setStatus.mutate('cancelled', {
      onSuccess: () => {
        setStatusLocal('cancelled');
        setConfirmCancel(false);
      },
      onError: (err) => onError(meetingErrorCode(err)),
    });
  }

  function doDelete() {
    remove.mutate(undefined, {
      onSuccess: () => navigate('/rencontres'),
      onError: (err) => onError(meetingErrorCode(err)),
    });
  }

  return (
    <details className={`${GS_COMPONENTS.card} p-5`}>
      <summary className="cursor-pointer text-field font-semibold text-slate-900 dark:text-slate-100">
        {t('gest:rencontres.edit.summary')}
      </summary>
      <div className="mt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.edit.title')}
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className={GS_COMPONENTS.input}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.edit.scheduled')}
          </span>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className={GS_COMPONENTS.input}
          />
          <span className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:rencontres.wizard.tzHint')}
          </span>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.edit.duration')}
          </span>
          <input
            type="number"
            min={0}
            max={1440}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
            className={GS_COMPONENTS.input}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.edit.location')}
          </span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={300}
            className={GS_COMPONENTS.input}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-700 dark:text-slate-300">
            {t('gest:rencontres.edit.status')}
          </span>
          <select
            value={status}
            onChange={(e) => setStatusLocal(e.target.value as MeetingStatus)}
            className={GS_COMPONENTS.input}
          >
            {(['upcoming', 'completed', 'cancelled'] as const).map((s) => (
              <option key={s} value={s}>{t(`gest:rencontres.status.${s}`)}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={save}
            disabled={update.isPending}
            className={GS_COMPONENTS.btnAction}
          >
            {t('gest:rencontres.edit.save')}
          </button>
          {status !== 'cancelled' ? (
            <button
              type="button"
              onClick={() => setConfirmCancel(true)}
              disabled={setStatus.isPending}
              className={GS_COMPONENTS.btnDangerOutline}
            >
              {t('gest:rencontres.edit.cancelMeeting')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={remove.isPending}
            className={GS_COMPONENTS.btnDangerOutline}
          >
            {t('gest:rencontres.edit.delete')}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmCancel}
        title={t('gest:rencontres.edit.confirmCancelTitle')}
        message={t('gest:rencontres.edit.confirmCancelMessage', { title: detail.meeting.title })}
        confirmLabel={t('gest:rencontres.edit.confirmCancelConfirm')}
        cancelLabel={t('gest:rencontres.edit.confirmDismiss')}
        variant="danger"
        isLoading={setStatus.isPending}
        onConfirm={doCancel}
        onCancel={() => setConfirmCancel(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title={t('gest:rencontres.edit.confirmDeleteTitle')}
        message={t('gest:rencontres.edit.confirmDeleteMessage', { title: detail.meeting.title })}
        confirmLabel={t('gest:rencontres.edit.confirmDeleteConfirm')}
        cancelLabel={t('gest:rencontres.edit.confirmDismiss')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </details>
  );
}

function DocumentsSection({
  detail,
  onError,
}: {
  detail: MeetingDetailResponse;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadMeetingDocument(detail.meeting.id);
  const remove = useDeleteMeetingDocument(detail.meeting.id);
  const [confirmDocId, setConfirmDocId] = useState<string | null>(null);

  function onFile(file: File | undefined) {
    if (!file) return;
    upload.mutate(file, { onError: (err) => onError(meetingErrorCode(err)) });
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <section className="space-y-3">
      <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
        {t('gest:rencontres.sections.documents', { total: detail.documents.length })}
      </h2>
      {detail.viewer.canEdit ? (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={DOC_ACCEPT}
            className="sr-only"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className={GS_COMPONENTS.btnSecondary}
          >
            {t('gest:rencontres.documents.upload')}
          </button>
        </div>
      ) : null}
      {detail.documents.length === 0 ? (
        <EmptyState
          icon={<Inbox size={48} aria-hidden="true" />}
          title={t('gest:rencontres.documents.empty')}
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
          {detail.documents.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileText size={18} className="shrink-0 text-slate-400" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="truncate text-field font-medium text-slate-900 dark:text-slate-100">
                    {doc.fileName}
                  </p>
                  <p className="text-meta text-slate-500 dark:text-slate-400">
                    {t('gest:rencontres.documents.meta', {
                      size: Math.round(doc.sizeBytes / 1024),
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}
                  onClick={() => {
                    void downloadBlobAs(
                      `/api/meetings/${detail.meeting.id}/documents/${doc.id}/download`,
                      doc.fileName,
                    );
                  }}
                >
                  <Download size={14} aria-hidden="true" />
                  {t('gest:rencontres.documents.download')}
                </button>
                {detail.viewer.canEdit ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDocId(doc.id)}
                    className={GS_COMPONENTS.btnDangerOutline}
                    aria-label={t('gest:rencontres.documents.remove', { name: doc.fileName })}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={!!confirmDocId}
        title={t('gest:rencontres.documents.confirmRemoveTitle')}
        message={t('gest:rencontres.documents.confirmRemoveMessage')}
        confirmLabel={t('gest:rencontres.documents.confirmRemoveConfirm')}
        cancelLabel={t('gest:rencontres.edit.confirmDismiss')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (!confirmDocId) return;
          remove.mutate(confirmDocId, {
            onSuccess: () => setConfirmDocId(null),
            onError: (err) => onError(meetingErrorCode(err)),
          });
        }}
        onCancel={() => setConfirmDocId(null)}
      />
    </section>
  );
}

export default function MeetingDetailPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailQ = useMeetingDetail(id, !!user);
  const errorCode = searchParams.get('error');

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  if (detailQ.isError && detailQ.error instanceof ApiForbiddenError) {
    return <MeetingsForbidden />;
  }

  if (detailQ.isLoading) {
    return (
      <div className="space-y-5">
        <SkeletonLine />
        <SkeletonCard />
      </div>
    );
  }

  if (detailQ.isError) {
    if (detailQ.error instanceof ApiServerError && detailQ.error.status === 404) {
      return (
        <EmptyState
          icon={<ShieldAlert size={48} aria-hidden="true" />}
          title={t('gest:rencontres.notFound.title')}
          description={t('gest:rencontres.notFound.description')}
          action={
            <Link to="/rencontres" className={GS_COMPONENTS.btnSecondary}>
              {t('gest:rencontres.notFound.back')}
            </Link>
          }
        />
      );
    }
    return (
      <QueryError message={t('gest:rencontres.loadError')} onRetry={() => void detailQ.refetch()} />
    );
  }

  if (!detailQ.data) return null;
  const { meeting, viewer } = detailQ.data;

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/rencontres"
          className="mb-3 inline-flex items-center gap-1.5 text-meta text-slate-500 transition-colors hover:text-slate-800 dark:hover:text-slate-200"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {t('gest:rencontres.notFound.back')}
        </Link>
        <PageHeader
          title={meeting.title}
          description={formatZonedDateTime(meeting.scheduledAt)}
          actions={<MeetingStatusBadge status={meeting.status} />}
        />
        {meeting.location ? (
          <p className="mt-1 text-field text-slate-500 dark:text-slate-400">{meeting.location}</p>
        ) : null}
        {meeting.durationMinutes != null ? (
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:rencontres.detail.duration', { n: meeting.durationMinutes })}
          </p>
        ) : null}
      </div>

      {errorCode ? (
        <div
          role="alert"
          className="rounded-xl border border-slate-200 border-l-3 border-l-red-500 bg-white px-4 py-3 text-field text-red-700 dark:border-slate-700 dark:border-l-red-500 dark:bg-slate-800 dark:text-red-300"
        >
          {t(`gest:rencontres.errors.${['invalid', 'forbidden', 'notfound', 'server'].includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {viewer.canEdit ? <EditMeetingPanel detail={detailQ.data} onError={setError} /> : null}
      <DocumentsSection detail={detailQ.data} onError={setError} />
    </div>
  );
}
